import { useMemo, useState } from "react";
import { Phone } from "lucide-react";
import { PHONE_DISPLAY, TEL_LINK, inr } from "@/lib/site";
import { Eyebrow, Reveal } from "./Reveal";

const Slider = ({ label, value, display, min, max, step, onChange, testid, pct }) => (
  <div>
    <div className="flex items-baseline justify-between mb-3">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <span data-testid={`${testid}-value`} className="font-mono text-lg font-semibold text-ink">
        {display}
      </span>
    </div>
    <input
      data-testid={testid}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ background: `linear-gradient(to right, #15803D ${pct}%, #E2E8F0 ${pct}%)` }}
      className="w-full"
    />
  </div>
);

export const EmiCalculator = () => {
  const [amount, setAmount] = useState(1000000);
  const [rate, setRate] = useState(9);
  const [years, setYears] = useState(15);

  const { emi, total, interest, principalPct } = useMemo(() => {
    const r = rate / 1200;
    const n = years * 12;
    const pow = Math.pow(1 + r, n);
    const emi = (amount * r * pow) / (pow - 1);
    const total = emi * n;
    return {
      emi,
      total,
      interest: total - amount,
      principalPct: (amount / total) * 100,
    };
  }, [amount, rate, years]);

  return (
    <section id="calculator" className="py-20 sm:py-28 bg-tint/70 border-y border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <Eyebrow>02 — EMI Calculator</Eyebrow>
          <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl tracking-tight text-ink">
            Pehle <em className="italic text-brand">plan</em> kijiye,
            <br />
            phir apply.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-md">
            Sliders ghumaiye aur turant dekhiye ki aapki monthly EMI kitni banegi.
            Phir ek call par apna loan confirm kijiye.
          </p>

          <div className="mt-10 space-y-8">
            <Slider
              label="Loan Amount"
              display={inr(amount)}
              value={amount}
              min={50000}
              max={10000000}
              step={50000}
              onChange={setAmount}
              testid="emi-amount-slider"
              pct={((amount - 50000) / (10000000 - 50000)) * 100}
            />
            <Slider
              label="Interest Rate (p.a.)"
              display={`${rate.toFixed(1)}%`}
              value={rate}
              min={7}
              max={18}
              step={0.1}
              onChange={setRate}
              testid="emi-rate-slider"
              pct={((rate - 7) / (18 - 7)) * 100}
            />
            <Slider
              label="Tenure"
              display={`${years} ${years === 1 ? "saal" : "saal"}`}
              value={years}
              min={1}
              max={30}
              step={1}
              onChange={setYears}
              testid="emi-tenure-slider"
              pct={((years - 1) / (30 - 1)) * 100}
            />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="emi-result-card"
            className="relative bg-ink text-paper rounded-[2rem] p-8 sm:p-10 shadow-2xl shadow-ink/25 overflow-hidden"
          >
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand/30 blur-3xl"
            />
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-paper/50">
              Monthly EMI
            </p>
            <p
              data-testid="emi-monthly-value"
              className="mt-2 font-display text-5xl sm:text-6xl font-semibold tracking-tight"
            >
              {inr(emi)}
            </p>

            <div className="mt-8 h-3 rounded-full overflow-hidden flex bg-white/10">
              <div
                data-testid="emi-principal-bar"
                className="h-full bg-brand transition-all duration-300"
                style={{ width: `${principalPct}%` }}
              />
              <div
                data-testid="emi-interest-bar"
                className="h-full bg-gold transition-all duration-300"
                style={{ width: `${100 - principalPct}%` }}
              />
            </div>

            <div className="mt-6 space-y-3.5 text-sm">
              <div className="flex justify-between border-b border-white/10 pb-3">
                <span className="inline-flex items-center gap-2 text-paper/70">
                  <span className="w-2.5 h-2.5 rounded-full bg-brand" /> Principal
                </span>
                <span data-testid="emi-principal-value" className="font-mono font-semibold">
                  {inr(amount)}
                </span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-3">
                <span className="inline-flex items-center gap-2 text-paper/70">
                  <span className="w-2.5 h-2.5 rounded-full bg-gold" /> Total Interest
                </span>
                <span data-testid="emi-interest-value" className="font-mono font-semibold">
                  {inr(interest)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-paper/70">Total Payable</span>
                <span data-testid="emi-total-value" className="font-mono font-semibold">
                  {inr(total)}
                </span>
              </div>
            </div>

            <a
              data-testid="emi-call-cta"
              href={TEL_LINK}
              className="mt-8 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-brand text-white px-6 py-4 text-base font-semibold hover:bg-brand-dark transition-colors"
            >
              <Phone className="w-4 h-4" />
              Is EMI par loan ke liye call karein — {PHONE_DISPLAY}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
